import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useOidc } from '@axa-fr/react-oidc';
import { CoralogixRum } from '@coralogix/browser';
import { Button, Text } from '@fly/core';
import { accessApiClientV1 } from '@fly/http-service';
import { PlatformService } from '@fly/platform-service';
import { COOKIE_NAMES, deleteCookie, getCookie } from '@fly/shared';
import { REGISTRATION_STORAGE_KEY } from '@fly/shared';
import { useClearQueryParams, useUser } from '@fly/shared-hooks';
import {
  closeDialog,
  openDialog,
  useGetCurrentUserProfileQuery,
} from '@fly/store';
import log from 'loglevel';

import { buildDesktopSigninUrl } from './desktopSigninUrl';
import { useMultipleTeamsToast } from './useMultipleTeamsToast';

// DESKTOP_SIGNIN_TOKEN_EXPIRY_SECS pins the Desktop sign-in token lifetime to
// one year so the Desktop app stays signed in across long stretches of
// inactivity. Without it, the JPD's Access default (~7 days) applies and users
// get logged out weekly. JFrog Access may cap this via
// tokens.expiry.max-token-lifetime; the response's actual expires_in is what
// we forward to the Desktop deep link, so any cap is honoured.
const DESKTOP_SIGNIN_TOKEN_EXPIRY_SECS = 365 * 24 * 60 * 60;

export default function LoginSuccessHandler() {
  const params = new URLSearchParams(window.location.search);
  const desktop = params.get('desktop');
  const clearQueryParams = useClearQueryParams();

  const noTenantId = getCookie(COOKIE_NAMES.NO_TENANT_ID);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { udid, username, email, firstLetterUserName } = useUser();
  const { logout, isAuthenticated } = useOidc();
  const { data: profileData } = useGetCurrentUserProfileQuery(undefined, {
    // Renders outside FlyOidcSecure, needs its own no-tenant guard
    skip: !isAuthenticated || noTenantId === 'true',
  });

  PlatformService.getInstance().setLogoutFunction(logout);
  useMultipleTeamsToast(isAuthenticated);

  useEffect(() => {
    if (email && desktop && profileData) {
      clearQueryParams(['desktop']);
      const exchangeToken = async () => {
        try {
          const { data } = await accessApiClientV1.post(
            '/tokens',
            {
              username: email,
              scope: profileData.isAdmin ? 'admins' : 'developers',
              refreshable: true,
              include_reference_token: true,
              description: 'oidc:desktop_app_signin',
              expires_in: DESKTOP_SIGNIN_TOKEN_EXPIRY_SECS,
            },
            { noToast: true }
          );
          const tenantId = getCookie(COOKIE_NAMES.TEAM_NAME);
          if (!tenantId) {
            // Don't open desktop app if team_name cookie doesn't exist
            log.warn(
              'Desktop authentication skipped: team_name cookie not found'
            );
            return;
          }
          window.location.href = buildDesktopSigninUrl({
            protocol: import.meta.env.VITE_ELECTRON_APP_PROTOCOL,
            token: data.access_token,
            username: email,
            tenantId,
            expiresIn: data.expires_in,
          });
        } catch (e) {
          log.error('Desktop authentication failed:', e);
          dispatch(
            openDialog({
              title: t('app.auth.error.title', {
                defaultValue: 'Authentication Failed',
              }),
              content: (
                <div>
                  <Text as='p' size='3' highContrast className='mb-fly-6'>
                    {t('app.auth.error.description', {
                      defaultValue:
                        "We couldn't complete your login. Please try again.",
                    })}
                  </Text>
                  <div className='flex flex-row justify-end gap-fly-3'>
                    <Button
                      variant='soft'
                      size='2'
                      color='neutral'
                      onClick={() => dispatch(closeDialog())}
                    >
                      {t('app.common.actions.ok', {
                        defaultValue: 'Ok',
                      })}
                    </Button>
                  </div>
                </div>
              ),
            })
          );
        }
      };
      exchangeToken();
    }
  }, [clearQueryParams, desktop, dispatch, email, profileData, t]);

  useEffect(() => {
    if (isAuthenticated) {
      // Clear sign-up tokens
      deleteCookie(COOKIE_NAMES.REGISTRATION_TOKEN);
      deleteCookie(COOKIE_NAMES.INVITATION_TOKEN_ID);
      deleteCookie(COOKIE_NAMES.INVITATION_TEAM);
      localStorage.removeItem(REGISTRATION_STORAGE_KEY);
      CoralogixRum.setUserContext({
        user_id: udid,
        user_name: username,
        user_metadata: {
          team: getCookie(COOKIE_NAMES.TEAM_NAME),
        },
      });
    }
  }, [username, email, firstLetterUserName, dispatch, isAuthenticated, udid]);

  return null;
}
